import RaidPlan, { RaidPlanTimeline } from 'app/frontend/types/custom/RaidPlan'
import { getEncounterAbilityAssignmentGroups } from 'app/frontend/types/custom/EncounterAbilityAssignmentDefaults'
import { getEncounterTimelineData } from 'app/frontend/base/services/raidPlanData'
import { EncounterAbilityRow } from '../../TeamPlans/hooks/useTeamPlanComputedData'

type ResolveLinkedPlanEncounterAssignmentRowsOptions = {
  raidPlan?: RaidPlan
  timeline: RaidPlanTimeline
  unknownAbilityLabel: string
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

const roundUpToThirtySeconds = (value: number) => Math.ceil(value / 30) * 30

export const resolveLinkedPlanTimelineMaxTime = ({
  raidPlan,
  timeline,
}: {
  raidPlan?: RaidPlan
  timeline: RaidPlanTimeline
}) => {
  const encounterTimeline = raidPlan
    ? getEncounterTimelineData()[raidPlan.encounterId.toString()]
    : undefined
  const encounterDifficulty =
    raidPlan && encounterTimeline
      ? encounterTimeline.difficulties.find(
          (difficulty) => difficulty.difficulty === raidPlan.difficulty,
        ) ?? encounterTimeline.difficulties[0]
      : undefined

  const encounterMax = encounterDifficulty?.maxTime ?? 0
  if (encounterMax > 0) {
    return roundUpToThirtySeconds(Math.max(60, encounterMax))
  }

  const encounterEntriesMax =
    encounterDifficulty?.phases
      .flatMap((phase) => phase.abilities)
      .flatMap((ability) =>
        ability.entries.map((entry) => entry.time + entry.duration),
      )
      .reduce((max, endAt) => Math.max(max, endAt), 0) ?? 0
  const actorMax = timeline.actors.reduce((max, actor) => {
    return actor.casts.reduce((innerMax, cast) => {
      if (cast.spell_id === null) return innerMax
      return Math.max(innerMax, cast.at)
    }, max)
  }, 0)

  return roundUpToThirtySeconds(Math.max(60, encounterEntriesMax, actorMax))
}

const computeCurrentPhaseBoundaries = (
  defaultPhaseBoundaries: Array<number | undefined>,
  maxTime: number,
  persistedPhaseBoundaries: unknown,
) => {
  const persisted = Array.isArray(persistedPhaseBoundaries)
    ? persistedPhaseBoundaries
    : []
  return defaultPhaseBoundaries.reduce<number[]>(
    (currentBoundaries, defaultBoundary, index) => {
      const prevCurrentBoundary =
        index === 0 ? 0 : currentBoundaries[index - 1] ?? 0
      const prevDefaultBoundary =
        index === 0 ? 0 : defaultPhaseBoundaries[index - 1] ?? 0
      const shiftBeforePhase = prevCurrentBoundary - prevDefaultBoundary
      const defaultBoundaryWithShift =
        (defaultBoundary ?? maxTime) + shiftBeforePhase
      const persistedBoundary = persisted[index]
      const rawBoundary =
        typeof persistedBoundary === 'number'
          ? persistedBoundary
          : defaultBoundaryWithShift
      const maxBoundary = Math.max(defaultBoundaryWithShift, rawBoundary)
      currentBoundaries.push(
        clamp(rawBoundary, prevCurrentBoundary, maxBoundary),
      )
      return currentBoundaries
    },
    [],
  )
}

export const resolveLinkedPlanEncounterAssignmentRows = ({
  raidPlan,
  timeline,
  unknownAbilityLabel,
}: ResolveLinkedPlanEncounterAssignmentRowsOptions): EncounterAbilityRow[] => {
  if (!raidPlan) return []
  const encounterTimeline =
    getEncounterTimelineData()[raidPlan.encounterId.toString()]
  if (!encounterTimeline) return []
  const encounterDifficulty =
    encounterTimeline.difficulties.find(
      (difficulty) => difficulty.difficulty === raidPlan.difficulty,
    ) ?? encounterTimeline.difficulties[0]
  if (!encounterDifficulty) return []
  const maxTime = resolveLinkedPlanTimelineMaxTime({ raidPlan, timeline })

  const defaultPhaseBoundaries = encounterDifficulty.phases.map((phase) => {
    return phase.abilities
      .flatMap((ability) =>
        ability.entries.map((entry) => entry.time + entry.duration),
      )
      .reduce<number | undefined>(
        (currentMax, endAt) =>
          currentMax === undefined ? endAt : Math.max(currentMax, endAt),
        undefined,
      )
  })

  const currentPhaseBoundaries = computeCurrentPhaseBoundaries(
    defaultPhaseBoundaries,
    maxTime,
    timeline.phase_boundaries ?? raidPlan.timeline?.phase_boundaries,
  )

  const originalFirstBySpellId = encounterDifficulty.phases
    .flatMap((phase) => phase.abilities)
    .reduce<Record<number, number>>((acc, ability) => {
      const firstAt =
        ability.entries.map((entry) => entry.time).sort((a, b) => a - b)[0] ?? 0
      if (
        acc[ability.spell_id] === undefined ||
        firstAt < acc[ability.spell_id]
      ) {
        acc[ability.spell_id] = firstAt
      }
      return acc
    }, {})

  return encounterDifficulty.phases
    .flatMap((phase, phaseIndex) => {
      const phaseStart =
        phaseIndex === 0 ? 0 : currentPhaseBoundaries[phaseIndex - 1] ?? 0
      const phaseEnd = currentPhaseBoundaries[phaseIndex] ?? maxTime
      const shiftBeforePhase =
        phaseIndex === 0
          ? 0
          : (currentPhaseBoundaries[phaseIndex - 1] ?? 0) -
            (defaultPhaseBoundaries[phaseIndex - 1] ?? 0)
      return phase.abilities.map((ability) => ({
        ...ability,
        entries: ability.entries
          .map((entry) => ({
            ...entry,
            time: Number((entry.time + shiftBeforePhase).toFixed(2)),
          }))
          .map((entry) => {
            const maxDuration = phaseEnd - entry.time
            return {
              ...entry,
              duration: Number(
                Math.max(Math.min(entry.duration, maxDuration), 0).toFixed(2),
              ),
            }
          })
          .filter(
            (entry) =>
              entry.time >= phaseStart &&
              entry.time < phaseEnd &&
              entry.duration > 0,
          ),
      }))
    })
    .reduce<EncounterAbilityRow[]>((rows, ability) => {
      const name = ability.name || unknownAbilityLabel
      const existing = rows.find((row) => row.spellId === ability.spell_id)

      if (!existing) {
        const entries = [...ability.entries].sort((a, b) => a.time - b.time)
        rows.push({
          spellId: ability.spell_id,
          name,
          color: ability.color,
          entries: entries.map((entry, index) => ({
            ...entry,
            castKey: `${ability.spell_id}:${index}`,
          })),
          assignmentGroups: getEncounterAbilityAssignmentGroups(
            ability.spell_id,
          ),
          first: entries[0]?.time ?? 0,
        })
        return rows
      }

      existing.entries = [...existing.entries, ...ability.entries]
        .sort((a, b) => a.time - b.time)
        .map((entry, index) => ({
          time: entry.time,
          duration: entry.duration,
          castKey: `${ability.spell_id}:${index}`,
        }))
      existing.assignmentGroups = Math.max(
        existing.assignmentGroups,
        getEncounterAbilityAssignmentGroups(ability.spell_id),
      )
      existing.first = existing.entries[0]?.time ?? existing.first
      return rows
    }, [])
    .sort((a, b) => {
      const originalA = originalFirstBySpellId[a.spellId] ?? a.first
      const originalB = originalFirstBySpellId[b.spellId] ?? b.first
      return originalA - originalB
    })
    .map((row) => ({
      ...row,
      assignmentGroups: Math.max(row.assignmentGroups, 1),
    }))
}
